import { useEffect, useState, useCallback, useRef } from "react";
import { Bell, Check, Trash2, Inbox, Package, Star, Gift, Eye, Volume2, VolumeX, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const ICONS = {
  new_request: { icon: Package, color: "#00F0FF" },
  new_review: { icon: Star, color: "#FFE600" },
  loyalty_milestone: { icon: Gift, color: "#39FF14" },
  order_tracked: { icon: Eye, color: "#A855F7" },
  daily_digest: { icon: CalendarDays, color: "#00F0FF" },
};

const timeAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "ora";
  if (d < 3600) return `${Math.floor(d / 60)} min fa`;
  if (d < 86400) return `${Math.floor(d / 3600)} h fa`;
  return `${Math.floor(d / 86400)} g fa`;
};

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem("s3d_notif_muted") === "1");
  const prevUnreadRef = useRef(null);

  const playSound = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
      o.start(); o.stop(ctx.currentTime + 0.47);
      o.onended = () => ctx.close();
    } catch {}
  };
  const toggleMute = () => setMuted((m) => { const nv = !m; localStorage.setItem("s3d_notif_muted", nv ? "1" : "0"); return nv; });

  const loadCount = useCallback(async () => {
    try {
      const r = await api.get("/notifications/unread_count");
      const c = r.data.count || 0;
      setUnread(c);
      const prev = prevUnreadRef.current;
      if (prev !== null && c > prev) {
        try {
          const list = await api.get("/notifications");
          setItems(list.data);
          const latest = list.data.find((n) => !n.read) || list.data[0];
          if (latest) {
            if (localStorage.getItem("s3d_notif_muted") !== "1") playSound();
            toast(latest.title, { description: latest.body, duration: 6000 });
          }
        } catch {}
      }
      prevUnreadRef.current = c;
    } catch {}
  }, []);
  const loadList = useCallback(async () => {
    try { const r = await api.get("/notifications"); setItems(r.data); } catch {}
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setItems((p) => p.map((n) => (n.id === id ? { ...n, read: true } : n)));
    loadCount();
  };
  const markAll = async () => { await api.put("/notifications/read_all"); setItems((p) => p.map((n) => ({ ...n, read: true }))); loadCount(); };
  const remove = async (id) => { await api.delete(`/notifications/${id}`); setItems((p) => p.filter((n) => n.id !== id)); loadCount(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" aria-label="Notifiche"
          className="relative w-10 h-10 flex items-center justify-center rounded-sm border border-white/15 text-white hover:border-neon-green hover:text-neon-green transition-colors duration-200">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="notification-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-neon-pink text-white"
              style={{ boxShadow: "0 0 8px rgba(255,16,240,0.7)" }}>
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid="notification-panel" align="end" className="w-80 sm:w-96 p-0 bg-card border-white/10 text-white rounded-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <span className="font-display font-bold text-sm tracking-tight">Notifiche</span>
          <div className="flex items-center gap-3">
            <button data-testid="notification-mute" onClick={toggleMute} title={muted ? "Riattiva suono" : "Silenzia suono"} aria-label={muted ? "Riattiva suono" : "Silenzia suono"} className="text-muted-foreground hover:text-neon-cyan transition-colors">
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            {items.some((n) => !n.read) && (
              <button data-testid="notification-mark-all" onClick={markAll} className="text-xs font-mono text-neon-cyan hover:text-white transition-colors">Segna tutte lette</button>
            )}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div data-testid="notification-empty" className="flex flex-col items-center py-12 text-muted-foreground">
              <Inbox className="w-8 h-8 mb-2 opacity-40" /><p className="text-sm font-light">Nessuna notifica.</p>
            </div>
          ) : (
            items.map((n, i) => {
              const cfg = ICONS[n.type] || { icon: Bell, color: "#39FF14" };
              const Icon = cfg.icon;
              return (
                <div key={n.id} data-testid={`notification-item-${i}`}
                  className={`flex gap-3 px-4 py-3 border-b border-white/5 transition-colors ${n.read ? "opacity-60" : "bg-white/[0.02]"}`}>
                  <div className="shrink-0 mt-0.5 w-8 h-8 flex items-center justify-center rounded-sm border" style={{ borderColor: `${cfg.color}55`, color: cfg.color }}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm text-white truncate">{n.title}</p>
                      {!n.read && <span className="shrink-0 w-2 h-2 rounded-full bg-neon-green" style={{ boxShadow: "0 0 6px #39FF14" }} />}
                    </div>
                    <p className="text-xs text-muted-foreground font-light mt-0.5 line-clamp-2">{n.body}</p>
                    <p className="text-[10px] font-mono text-muted-foreground/70 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {!n.read && (
                      <button data-testid={`notification-read-${i}`} onClick={() => markRead(n.id)} title="Segna letta" className="text-muted-foreground hover:text-neon-green transition-colors"><Check className="w-4 h-4" /></button>
                    )}
                    <button data-testid={`notification-del-${i}`} onClick={() => remove(n.id)} title="Elimina" className="text-muted-foreground hover:text-neon-pink transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
